import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiService, HttpMethod } from '../api.service';
import { map } from 'rxjs/operators';
import { VendorList } from './vendor-list.model';
import { Vendor } from './vendor.model';
import { SasTokenModel } from '../../shared-api/images/sas-token.model';
import { AssignedImageResult } from '../converter/assigned-image-result.model';
import { Contact } from './contact.model';
import { UserNotificationMethod } from '../user/user.model';
import { CalculatePricesResult } from './calculate-prices.model';
import { VendorMapInfo } from './vendor-map-info.model';
import { PagingResponse, VendorForNewPoResponse } from './models/vendor-for-new-po.model';
import { PagingRequest } from 'src/app/shared-api/models/paging.model';
import { AdminVendorListItemModel } from './models/admin-vendor.model';
import { ContactListItem, VendorDetailsModel } from './models/vendor-details.model';
import { ContactDetailsModel } from './models/contact-details.model';

@Injectable({
    providedIn: 'root'
})
export class VendorApiService extends ApiService {
    constructor(protected http: HttpClient) {
        super(http);
    }
    updatePricingNotes(notes: string, vendorId: number): Observable<any> {
        let body = { vendorId, notes }
        return this.apiRequest<any>(this.updatePricingNotesUrl, HttpMethod.Put, JSON.stringify(body));
    }
    getAllVendors(locationId: number, includeAssayVendors: boolean, includePricing: boolean, includeHedges: boolean,
        includeSubVendors: boolean, pageIndex: number = 0, pageSize: number = 0, searchString: string = '', sortField: string = 'name', sortOrder: string = 'asc'): Observable<VendorList> {
        return this.apiRequest<VendorList>(
            this.getAllVendorsUrl(locationId, includeAssayVendors, includePricing, includeHedges, includeSubVendors, pageIndex, pageSize, searchString, sortField, sortOrder),
            HttpMethod.Get, undefined, 60).pipe(map(json => new VendorList(json)));
    }

    getMapInfo(locationId: number): Observable<VendorMapInfo[]> {
        return this.apiRequest<any>(this.getVendorMapInfoUrl(locationId), HttpMethod.Get)
            .pipe(map(json => json.map(item => new VendorMapInfo(item))));
    }

    getVendor(vendorId: number): Observable<Vendor> {
        return this.apiRequest<Vendor>(this.getVendorUrl(vendorId), HttpMethod.Get).pipe(map(json => new Vendor(json)));
    }

    getVendorWithEffectivePricing(vendorId: number): Observable<Vendor> {
        return this.apiRequest<Vendor>(this.getVendorWithEffectivePricingUrl(vendorId), HttpMethod.Get).pipe(map(json => new Vendor(json)));
    }

    getHedgeInfo(locationId: number): Observable<Vendor[]> {
        return this.apiRequest<Vendor[]>(this.getHedgeInfoUrl(locationId), HttpMethod.Get)
            .pipe(map(json => json.map(item => new Vendor(item))));
    }

    addVendor(vendor: Vendor, locationId: number): Observable<Vendor> {
        const updateObj = Object.assign({ ...vendor }, { locationId });
        return this.apiRequest(this.addVendorUrl, HttpMethod.Post, JSON.stringify(updateObj))
            .pipe(map(json => new Vendor(json)));
    }

    updateVendor(vendor: Vendor): Observable<Vendor> {
        return this.apiRequest(this.updateVendorUrl, HttpMethod.Put, JSON.stringify(vendor))
            .pipe(map(json => new Vendor(json)));
    }

    deleteVendor(vendorId: number): Observable<null> {
        return this.apiRequest(this.deleteVendorUrl(vendorId), HttpMethod.Delete);
    }

    addContact(contact: Contact, vendorId: number): Observable<Contact> {
        const updateObj = Object.assign({ ...contact }, { vendorId });
        return this.apiRequest(this.addContactUrl, HttpMethod.Post, JSON.stringify(updateObj))
            .pipe(map(json => new Contact(json)));
    }

    updateContact(contact: Contact): Observable<Contact> {
        return this.apiRequest(this.updateContactUrl, HttpMethod.Put, JSON.stringify(contact))
            .pipe(map(json => new Contact(json)));
    }

    deleteContact(contactId: number): Observable<null> {
        return this.apiRequest(this.deleteContactUrl(contactId), HttpMethod.Delete);
    }

    getSasTokenUrl(filename: string): Observable<SasTokenModel> {
        return this.apiRequest<SasTokenModel>(this.getSasTokenUrlUrl(filename), HttpMethod.Get)
            .pipe(map(response => new SasTokenModel(response)));
    }

    licenseImageUploaded(vendorId: number, url: string): Observable<AssignedImageResult> {
        const info = { id: vendorId, url };
        return this.apiRequest(this.licenseImageUploadedUrl, HttpMethod.Put, JSON.stringify(info));
    }

    removeLicenseImage(vendorId: number, url: string): Observable<any> {
        const info = { id: vendorId, url };
        return this.apiRequest(this.removeLicenseImageUrl, HttpMethod.Put, JSON.stringify(info));
    }

    logoUploaded(vendorId: number, url: string): Observable<AssignedImageResult> {
        const info = { id: vendorId, url };
        return this.apiRequest(this.logoUploadedUrl, HttpMethod.Put, JSON.stringify(info));
    }

    removeLogo(vendorId: number): Observable<any> {
        const info = { id: vendorId };
        return this.apiRequest(this.removeLogoUrl, HttpMethod.Put, JSON.stringify(info));
    }

    updatePricingNotification(vendorId: number, method: UserNotificationMethod): Observable<any> {
        const info = { id: vendorId, method };
        return this.apiRequest(this.updatePricingNotificationUrl, HttpMethod.Put, JSON.stringify(info));
    }

    getPricingNotes(vendorId: number): Observable<string> {
        return this.http.get(this.getPricingNotesUrl(vendorId), { responseType: 'text' });
    }
    updateContractNotification(vendorId: number, method: UserNotificationMethod): Observable<any> {
        const info = { id: vendorId, method };
        return this.apiRequest(this.updateContractNotificationUrl, HttpMethod.Put, JSON.stringify(info));
    }

    updateStatusUpdateNotification(vendorId: number, method: UserNotificationMethod): Observable<any> {
        const info = { id: vendorId, method };
        return this.apiRequest(this.updateStatusUpdateNotificationUrl, HttpMethod.Put, JSON.stringify(info));
    }

    contactDriversLicenseImageUploaded(contactId: number, url: string): Observable<AssignedImageResult> {
        const info = { id: contactId, url };
        return this.apiRequest(this.contactDriversLicenseImageUploadedUrl, HttpMethod.Put, JSON.stringify(info));
    }

    calculatePrices(platinumPercent: number, palladiumPercent: number, rhodiumPercent: number, weight: number):
        Observable<CalculatePricesResult> {
        const info = { platinumPercent, palladiumPercent, rhodiumPercent, weight };
        return this.apiRequest(this.calculatePricesUrl, HttpMethod.Put, JSON.stringify(info))
            .pipe(map(response => new CalculatePricesResult(response)));
    }

    // ------ V2 Api ------
    getVendorsForNewPo(locationId: number, includeSubVendors: boolean, options?: PagingRequest): Observable<PagingResponse<VendorForNewPoResponse>> {
        return this.apiRequest(this.vendorsForNewPoUrl(locationId, includeSubVendors, options), HttpMethod.Get)
    }

    getAdminVendors(locationId: number, subsidiaryId: number, options?: PagingRequest): Observable<PagingResponse<AdminVendorListItemModel>> {
        return this.apiRequest(this.adminVendorsUrl(locationId, subsidiaryId, options), HttpMethod.Get)
    }

    getAdminVendorDetails(vendorId: number): Observable<VendorDetailsModel> {
        return this.apiRequest(this.adminVendorDetailsUrl(vendorId), HttpMethod.Get);
    }
    getContactDetails(contactId: number): Observable<ContactDetailsModel> {
        return this.apiRequest(this.contactDetailsUrl(contactId), HttpMethod.Get);
    }
    adminUpdateVendor(vendor: Vendor): Observable<AdminVendorListItemModel> {
        return this.apiRequest(this.adminUpdateVendorUrl(vendor.id), HttpMethod.Put, JSON.stringify(vendor));
    }
    adminAddVendor(vendor: Vendor, locationId: number): Observable<AdminVendorListItemModel> {
        let body = Object.assign({ ...vendor }, { locationId });
        return this.apiRequest(this.adminAddVendorUrl(), HttpMethod.Post, JSON.stringify(body));
    }
    adminUpdateContact(contact: Contact, vendorId: number): Observable<ContactListItem> {
        return this.apiRequest(this.adminUpdateContactUrl(contact.id, vendorId), HttpMethod.Put, JSON.stringify(contact))
    }
    adminAddContact(contact: Contact, vendorId: number): Observable<ContactListItem> {
        return this.apiRequest(this.adminAddContactUrl(vendorId), HttpMethod.Post, JSON.stringify(contact));
    }
    // =============================================================================================================================
    // URLs
    // =============================================================================================================================
    /* tslint:disable:member-ordering */
    // tslint:disable:max-line-length
    private get baseUrl(): string { return this.baseApiUrl + 'vendors'; }
    private get baseContactUrl(): string { return this.baseApiUrl + 'vendors/contact'; }
    private getVendorUrl(id: number): string { return `${this.baseUrl}/${id}`; }
    private getVendorWithEffectivePricingUrl(id: number): string { return `${this.baseUrl}/${id}/effectivePricing`; }
    private getAllVendorsUrl(locationId: number, includeAssayVendors: boolean, includePricing: boolean, includeHedges: boolean, includeSubVendors: boolean, pageIndex: number = 0, pageSize: number = 0, searchString: string = '', sortField: string = 'name', sortOrder: string = 'asc'): string {
        let url = `${this.baseUrl}/all?locationId=${locationId}&includeAssayVendors=${includeAssayVendors}&includePricing=${includePricing}&includeHedges=${includeHedges}&includeSubVendors=${includeSubVendors}&sortField=${sortField}&sortOrder=${sortOrder}&pageIndex=${pageIndex}&pageSize=${pageSize}`;
        if (searchString !== '')
            url = url + '&searchString=' + searchString;
        return url;
    }
    private getVendorMapInfoUrl(locationid: number): string {
        return `${this.baseUrl}/mapInfo?locationId=${locationid}`;
    }
    private getHedgeInfoUrl(locationid: number): string { return `${this.baseUrl}/hedges?locationId=${locationid}`; }
    private get addVendorUrl(): string { return `${this.baseUrl}`; }
    private get updateVendorUrl(): string { return `${this.baseUrl}`; }
    private deleteVendorUrl(id: number): string { return `${this.baseUrl}/${id}`; }
    private get addContactUrl(): string { return `${this.baseContactUrl}`; }
    private get updateContactUrl(): string { return `${this.baseContactUrl}`; }
    private deleteContactUrl(id: number): string { return `${this.baseContactUrl}/${id}`; }
    private getSasTokenUrlUrl(filename: string): string { return `${this.baseUrl}/sas/${filename}`; }
    private get licenseImageUploadedUrl(): string { return `${this.baseUrl}/licenseImageUploaded`; }
    private get removeLicenseImageUrl(): string { return `${this.baseUrl}/removeLicenseImage`; }
    private get logoUploadedUrl(): string { return `${this.baseUrl}/logoUploaded`; }
    private get removeLogoUrl(): string { return `${this.baseUrl}/removeLogo`; }
    private get updatePricingNotificationUrl(): string { return `${this.baseUrl}/pricingNotification`; }
    private get updateContractNotificationUrl(): string { return `${this.baseUrl}/contractNotification`; }
    private get updateStatusUpdateNotificationUrl(): string { return `${this.baseUrl}/statusUpdateNotification`; }
    private get contactDriversLicenseImageUploadedUrl(): string { return `${this.baseContactUrl}/driversLicenseImageUploaded`; }
    private get calculatePricesUrl(): string { return `${this.baseUrl}/calculatePrices`; }
    private get updatePricingNotesUrl(): string { return `${this.baseUrl}/notes`; }
    private getPricingNotesUrl(vendorId: number): string { return `${this.baseUrl}/notes?vendorId=${vendorId}`; }
    

    private baseApiV2 = this.baseApiUrl + 'v2'
    private vendorsForNewPoUrl(locationId: number, includeSubVendors: boolean, options?: PagingRequest): string { 
        let url = `${this.baseApiV2}/vendors/newPo?locationId=${locationId}&includeSubVendors=${includeSubVendors}`;   
        if(!options)
            return url;

        url = url + '&' + this.buildPagingRequest(options); 
        return url;
    }
    private adminVendorsUrl(locationId: number, subsidiaryId: number, options?: PagingRequest): string { 
        let url = `${this.baseApiV2}/admin/vendors?locationId=${locationId}&subsidiaryId=${subsidiaryId}`;   
        if(!options)
            return url;

        url = url + '&' + this.buildPagingRequest(options); 
        return url;
    }
    private adminVendorDetailsUrl(vendorId: number): string { 
        return `${this.baseApiV2}/admin/vendors/` + vendorId;
    }
    private contactDetailsUrl(contactId: number): string { 
        return `${this.baseApiV2}/admin/vendors/contacts/${contactId}`;
    }
    private adminUpdateVendorUrl(vendorId: number): string { 
        return `${this.baseApiV2}/admin/vendors/${vendorId}`;
    }
    private adminAddVendorUrl(): string { 
        return `${this.baseApiV2}/admin/vendors`;
    }
    private adminUpdateContactUrl(contactId: number, vendorId: number): string { 
        return `${this.baseApiV2}/admin/vendors/${vendorId}/contacts/${contactId}`;
    }
    private adminAddContactUrl(vendorId: number): string { 
        return `${this.baseApiV2}/admin/vendors/${vendorId}/contacts`;
    }
}
